import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
  LinearProgress,
  Divider,
  Tab,
  Tabs,
  Stack,
  Alert,
} from '@mui/material';
import {
  Close,
  Router as RouterIcon,
  People,
  Wifi,
  Memory,
  Speed,
  Computer,
  NetworkCheck,
  PersonAdd,
  PersonOff,
  WifiOff,
  Dashboard,
  Refresh,
} from '@mui/icons-material';
import { format } from 'date-fns';
import routerServiceV6 from '../services/routerServiceV6';
import routerServiceV7 from '../services/routerServiceV7';
import PPPConnectionsDialog from './PPPConnectionsDialog';

interface RouterDetailDialogProps {
  open: boolean;
  onClose: () => void;
  router: any;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`router-tabpanel-${index}`}
      aria-labelledby={`router-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const RouterDetailDialog: React.FC<RouterDetailDialogProps> = ({ open, onClose, router }) => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [routerDetails, setRouterDetails] = useState<any>(null);
  const [pppDialogOpen, setPppDialogOpen] = useState(false);
  const [liveData, setLiveData] = useState({
    cpu: 0,
    memory: 0,
    uptime: '',
    lastUpdated: new Date()
  });

  // Fetch router details from API
  const fetchRouterDetails = async () => {
    if (!router?.id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching router details for:', router.id);
      const service = router.os_version === '7' ? routerServiceV7 : routerServiceV6;
      const response = await service.getRouterDetails(router.id);
      
      console.log('Router details response:', response);
      
      if (response.data && response.data.success && response.data.data) {
        const details = response.data.data;
        setRouterDetails(details);
        
        // Update live data
        if (details.resources) {
          setLiveData({
            cpu: details.resources.cpu || 0,
            memory: details.resources.memory || 0,
            uptime: details.resources.uptime || '',
            lastUpdated: new Date()
          });
        }
      } else {
        setError('Data router tidak valid');
      }
    } catch (err: any) {
      console.error('Error fetching router details:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch router details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch live resources data
  const fetchLiveResources = async () => {
    if (!router?.id || !routerDetails) return;
    
    try {
      const service = router.os_version === '7' ? routerServiceV7 : routerServiceV6;
      const response = await service.getRouterResources(router.id);
      
      if (response.data && response.data.success && response.data.data) {
        const resources = response.data.data;
        setLiveData({
          cpu: resources.cpu || 0,
          memory: resources.memory || 0,
          uptime: resources.uptime || '',
          lastUpdated: new Date()
        });
      }
    } catch (err) {
      console.error('Error fetching live resources:', err);
    }
  };

  // Load data when dialog opens
  useEffect(() => {
    if (open && router?.id) {
      fetchRouterDetails();
    }
  }, [open, router?.id]);

  // Live updates for CPU and Memory
  useEffect(() => {
    if (!open || !routerDetails) return;

    const interval = setInterval(() => {
      fetchLiveResources();
    }, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, [open, routerDetails, router?.id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getPercentageColor = (percentage: number) => {
    if (percentage < 30) return 'success';
    if (percentage < 70) return 'warning';
    return 'error';
  };

  const formatTrafficValue = (value: string) => {
    return value === '0 B' ? 'No Data' : value;
  };

  if (!router) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, height: '90vh' }
      }}
    >
      <DialogTitle sx={{ position: 'relative', pb: 0 }}>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}
        >
          <Close />
        </IconButton>
        
        <Box display="flex" alignItems="center" gap={2} pt={1}>
          <RouterIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {router.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {router.host || router.ip_address}:{router.port}
            </Typography>
          </Box>
          <Chip 
            label={router.status === 'active' ? 'Online' : 'Offline'} 
            color={router.status === 'active' ? 'success' : 'error'}
            icon={router.status === 'active' ? <NetworkCheck /> : <WifiOff />}
          />
        </Box>

        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          sx={{ mt: 2 }}
          variant="scrollable"
        >
          <Tab label="Overview" icon={<Dashboard />} />
          <Tab label="PPP Users" icon={<People />} />
          <Tab label="Hotspot" icon={<Wifi />} />
          <Tab label="System Info" icon={<Computer />} />
          <Tab label="Network" icon={<NetworkCheck />} />
        </Tabs>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" height="200px">
            <LinearProgress sx={{ width: '50%' }} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6">Connection Error</Typography>
            <Typography variant="body2">{error}</Typography>
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={fetchRouterDetails}
              sx={{ mt: 2 }}
              startIcon={<Refresh />}
            >
              Retry
            </Button>
          </Alert>
        )}

        {!loading && !error && routerDetails && (
          <>
            {/* Overview Tab */}
            <TabPanel value={tabValue} index={0}>
              <Stack spacing={3}>
                <Typography variant="h6" color="primary">Live System Metrics</Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Box flex={1} minWidth={300}>
                    <Card sx={{ bgcolor: 'grey.50' }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <Speed />
                          <Typography variant="subtitle2">CPU Usage</Typography>
                          <Chip 
                            label={`${liveData.cpu}%`} 
                            size="small" 
                            color={getPercentageColor(liveData.cpu) as any}
                          />
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={liveData.cpu} 
                          color={getPercentageColor(liveData.cpu) as any}
                          sx={{ height: 8, borderRadius: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Updated: {format(liveData.lastUpdated, 'HH:mm:ss')}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                  <Box flex={1} minWidth={300}>
                    <Card sx={{ bgcolor: 'grey.50' }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <Memory />
                          <Typography variant="subtitle2">Memory Usage</Typography>
                          <Chip 
                            label={`${liveData.memory}%`} 
                            size="small" 
                            color={getPercentageColor(liveData.memory) as any}
                          />
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={liveData.memory} 
                          color={getPercentageColor(liveData.memory) as any}
                          sx={{ height: 8, borderRadius: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Updated: {format(liveData.lastUpdated, 'HH:mm:ss')}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                </Box>

                <Divider />

                <Typography variant="h6" color="primary">Network Summary</Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Card sx={{ flex: 1, minWidth: 200, bgcolor: 'primary.50' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <People sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                      <Typography variant="h4" color="primary.main" fontWeight="bold">
                        {routerDetails.pppSecrets?.total || 0}
                      </Typography>
                      <Typography variant="subtitle2">PPP Users</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {routerDetails.pppSecrets?.active || 0} active, {routerDetails.pppSecrets?.disabled || 0} disabled
                      </Typography>
                    </CardContent>
                  </Card>
                  
                  <Card sx={{ flex: 1, minWidth: 200, bgcolor: 'success.50' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <NetworkCheck sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                      <Typography variant="h4" color="success.main" fontWeight="bold">
                        {routerDetails.pppConnections?.active || 0}
                      </Typography>
                      <Typography variant="subtitle2">Active PPP</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Currently connected
                      </Typography>
                    </CardContent>
                  </Card>
                  
                  <Card sx={{ flex: 1, minWidth: 200, bgcolor: 'info.50' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Wifi sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />
                      <Typography variant="h4" color="info.main" fontWeight="bold">
                        {routerDetails.hotspotUsers?.total || 0}
                      </Typography>
                      <Typography variant="subtitle2">Hotspot Users</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {routerDetails.hotspotUsers?.active || 0} online
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>

                <Divider />

                <Typography variant="h6" color="primary">Interface Overview</Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Card sx={{ flex: 1, minWidth: 200, bgcolor: 'grey.50' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Computer sx={{ fontSize: 32, color: 'grey.600', mb: 1 }} />
                      <Typography variant="h4" color="grey.700" fontWeight="bold">
                        {routerDetails.interfaces ? routerDetails.interfaces.length : 0}
                      </Typography>
                      <Typography variant="subtitle2">Total Interfaces</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Physical & Virtual
                      </Typography>
                    </CardContent>
                  </Card>
                  
                  <Card sx={{ flex: 1, minWidth: 200, bgcolor: 'success.50' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <NetworkCheck sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                      <Typography variant="h4" color="success.main" fontWeight="bold">
                        {routerDetails.interfaces ? routerDetails.interfaces.filter((iface: any) => iface.status === 'running').length : 0}
                      </Typography>
                      <Typography variant="subtitle2">Active Interfaces</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Currently running
                      </Typography>
                    </CardContent>
                  </Card>
                  
                  <Card sx={{ flex: 1, minWidth: 200, bgcolor: 'warning.50' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <WifiOff sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
                      <Typography variant="h4" color="warning.main" fontWeight="bold">
                        {routerDetails.interfaces ? routerDetails.interfaces.filter((iface: any) => iface.status === 'disabled').length : 0}
                      </Typography>
                      <Typography variant="subtitle2">Disabled Interfaces</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Not in use
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>

                {/* System Information */}
                <Divider />
                <Typography variant="h6" color="primary">System Information</Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <Card sx={{ flex: 1, minWidth: 300, bgcolor: 'grey.50' }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom color="primary" fontWeight="bold">
                        {routerDetails.systemInfo?.routerIdentity || 'Unknown Router'}
                      </Typography>
                      <Stack spacing={1}>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">Model:</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {routerDetails.systemInfo?.boardName || 'N/A'}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">Version:</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {routerDetails.systemInfo?.version || 'N/A'}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">Architecture:</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {routerDetails.systemInfo?.architecture || 'N/A'}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">Uptime:</Typography>
                          <Typography variant="body2" fontWeight="medium" color="success.main">
                            {liveData.uptime}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Box>
              </Stack>
            </TabPanel>

            {/* PPP Users Tab */}
            <TabPanel value={tabValue} index={1}>
              {routerDetails?.pppSecrets && routerDetails?.pppConnections ? (
                <Stack spacing={3}>
                  <Box display="flex" gap={3} flexWrap="wrap">
                    <Box flex={1} minWidth={250}>
                      <Card sx={{ bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                          <PersonAdd sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                          <Typography variant="h3" color="success.main" fontWeight="bold">
                            {routerDetails.pppSecrets.active}
                          </Typography>
                          <Typography variant="h6" color="success.800">Active PPP Secrets</Typography>
                          <Typography variant="body2" color="text.secondary">Users enabled and ready</Typography>
                        </CardContent>
                      </Card>
                    </Box>
                    <Box flex={1} minWidth={250}>
                      <Card sx={{ bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                          <PersonOff sx={{ fontSize: 48, color: 'error.main', mb: 2 }} />
                          <Typography variant="h3" color="error.main" fontWeight="bold">
                            {routerDetails.pppSecrets.disabled}
                          </Typography>
                          <Typography variant="h6" color="error.800">Disabled PPP Secrets</Typography>
                          <Typography variant="body2" color="text.secondary">Users temporarily disabled</Typography>
                        </CardContent>
                      </Card>
                    </Box>
                    <Box flex={1} minWidth={250}>
                      <Card sx={{ bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                          <NetworkCheck sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                          <Typography variant="h3" color="primary.main" fontWeight="bold">
                            {routerDetails.pppConnections.active}
                          </Typography>
                          <Typography variant="h6" color="primary.800">Active Connections</Typography>
                          <Typography variant="body2" color="text.secondary">Currently connected</Typography>
                        </CardContent>
                      </Card>
                    </Box>
                  </Box>
                  
                  <Divider />
                  
                  <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
                    <Typography variant="h6" color="primary">
                      Want to see detailed active connections?
                    </Typography>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      size="large"
                      onClick={() => setPppDialogOpen(true)}
                      startIcon={<People />}
                    >
                      View Active PPP Connections
                    </Button>
                  </Box>
                </Stack>
              ) : (
                <Typography color="text.secondary">Data tidak tersedia untuk tab ini.</Typography>
              )}
            </TabPanel>

            {/* Hotspot Tab */}
            <TabPanel value={tabValue} index={2}>
              {routerDetails?.hotspotUsers ? (
                <Box display="flex" gap={3} flexWrap="wrap">
                  <Box flex={1} minWidth={250}>
                    <Card sx={{ bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <People sx={{ fontSize: 48, color: 'info.main', mb: 2 }} />
                        <Typography variant="h3" color="info.main" fontWeight="bold">
                          {routerDetails.hotspotUsers.total}
                        </Typography>
                        <Typography variant="h6" color="info.800">Total Hotspot Users</Typography>
                        <Typography variant="body2" color="text.secondary">Registered accounts</Typography>
                      </CardContent>
                    </Card>
                  </Box>
                  <Box flex={1} minWidth={250}>
                    <Card sx={{ bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Wifi sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                        <Typography variant="h3" color="success.main" fontWeight="bold">
                          {routerDetails.hotspotUsers.active}
                        </Typography>
                        <Typography variant="h6" color="success.800">Active Sessions</Typography>
                        <Typography variant="body2" color="text.secondary">Currently online</Typography>
                      </CardContent>
                    </Card>
                  </Box>
                  <Box flex={1} minWidth={250}>
                    <Card sx={{ bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <WifiOff sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
                        <Typography variant="h3" color="warning.main" fontWeight="bold">
                          {routerDetails.hotspotUsers.disabled}
                        </Typography>
                        <Typography variant="h6" color="warning.800">Inactive Users</Typography>
                        <Typography variant="body2" color="text.secondary">Offline accounts</Typography>
                      </CardContent>
                    </Card>
                  </Box>
                </Box>
              ) : (
                <Typography color="text.secondary">Data tidak tersedia untuk tab ini.</Typography>
              )}
            </TabPanel>

            {/* System Info Tab */}
            <TabPanel value={tabValue} index={3}>
              {routerDetails?.systemInfo ? (
                <Stack spacing={3}>
                  <Box display="flex" gap={3} flexWrap="wrap">
                    <Box flex={1} minWidth={300}>
                      <Card sx={{ bgcolor: 'grey.50' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom color="primary">Hardware Information</Typography>
                          <Stack spacing={2}>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">Board Name:</Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {routerDetails.systemInfo.boardName}
                              </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">Architecture:</Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {routerDetails.systemInfo.architecture}
                              </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">Router Identity:</Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {routerDetails.systemInfo.routerIdentity}
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Box>
                    <Box flex={1} minWidth={300}>
                      <Card sx={{ bgcolor: 'grey.50' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom color="primary">Software Information</Typography>
                          <Stack spacing={2}>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">Version:</Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {routerDetails.systemInfo.version}
                              </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">Uptime:</Typography>
                              <Typography variant="body2" fontWeight="medium">{liveData.uptime}</Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">CPU Usage:</Typography>
                              <Typography variant="body2" fontWeight="medium" color={liveData.cpu > 70 ? 'error.main' : 'success.main'}>
                                {liveData.cpu}%
                              </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">Memory Usage:</Typography>
                              <Typography variant="body2" fontWeight="medium" color={liveData.memory > 80 ? 'error.main' : 'success.main'}>
                                {liveData.memory}%
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Box>
                  </Box>
                </Stack>
              ) : (
                <Typography color="text.secondary">Data tidak tersedia untuk tab ini.</Typography>
              )}
            </TabPanel>

            {/* Network Tab */}
            <TabPanel value={tabValue} index={4}>
              {routerDetails?.traffic && Array.isArray(routerDetails?.interfaces) ? (
                <Stack spacing={3}>
                  <Box display="flex" gap={3} flexWrap="wrap">
                    <Box flex={1} minWidth={300}>
                      <Card sx={{ bgcolor: 'grey.50' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom color="primary">Traffic Summary</Typography>
                          <Stack spacing={2}>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">Total RX:</Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {formatTrafficValue(routerDetails.traffic.totalRx)}
                              </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">Total TX:</Typography>
                              <Typography variant="body2" fontWeight="medium">
                                {formatTrafficValue(routerDetails.traffic.totalTx)}
                              </Typography>
                            </Box>
                            <Divider />
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">Current RX:</Typography>
                              <Typography variant="body2" fontWeight="medium" color="success.main">
                                {routerDetails.traffic.currentRx}
                              </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">Current TX:</Typography>
                              <Typography variant="body2" fontWeight="medium" color="error.main">
                                {routerDetails.traffic.currentTx}
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Box>
                    <Box flex={1} minWidth={300}>
                      <Card sx={{ bgcolor: 'grey.50' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom color="primary">
                            Active Interfaces ({routerDetails.interfaces.filter((iface: any) => iface.status === 'running').length})
                          </Typography>
                          <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                            <Stack spacing={2}>
                              {routerDetails.interfaces
                                .filter((iface: any) => iface.status === 'running')
                                .slice(0, 10)
                                .map((iface: any, index: number) => (
                                <Box key={index} display="flex" alignItems="center" justifyContent="space-between">
                                  <Box flex={1}>
                                    <Typography variant="body2" fontWeight="medium" noWrap>
                                      {iface.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      ↓ {formatTrafficValue(iface.rxBytes)} | ↑ {formatTrafficValue(iface.txBytes)}
                                    </Typography>
                                  </Box>
                                  <Chip 
                                    label="Running" 
                                    size="small" 
                                    color="success"
                                    sx={{ ml: 1 }}
                                  />
                                </Box>
                              ))}
                              {routerDetails.interfaces.filter((iface: any) => iface.status === 'running').length > 10 && (
                                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', fontStyle: 'italic' }}>
                                  ... and {routerDetails.interfaces.filter((iface: any) => iface.status === 'running').length - 10} more interfaces
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  </Box>
                  
                  {/* Top Traffic Interfaces */}
                  <Card sx={{ bgcolor: 'grey.50' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">
                        Top Traffic Interfaces
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap">
                        {routerDetails.interfaces
                          .filter((iface: any) => iface.status === 'running' && (iface.rxBytes !== '0 B' || iface.txBytes !== '0 B'))
                          .sort((a: any, b: any) => {
                            // Convert to bytes for proper sorting
                            const aBytes = a.rxBytes === '0 B' ? 0 : parseFloat(a.rxBytes.split(' ')[0]) || 0;
                            const bBytes = b.rxBytes === '0 B' ? 0 : parseFloat(b.rxBytes.split(' ')[0]) || 0;
                            return bBytes - aBytes;
                          })
                          .slice(0, 6)
                          .map((iface: any, index: number) => (
                            <Card key={index} sx={{ minWidth: 250, flex: 1, border: '1px solid', borderColor: 'primary.200' }}>
                              <CardContent sx={{ p: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold" color="primary" noWrap>
                                  {iface.name}
                                </Typography>
                                <Stack spacing={1} sx={{ mt: 1 }}>
                                  <Box display="flex" justifyContent="space-between">
                                    <Typography variant="caption" color="success.main">Downloaded:</Typography>
                                    <Typography variant="caption" fontWeight="medium">
                                      {formatTrafficValue(iface.rxBytes)}
                                    </Typography>
                                  </Box>
                                  <Box display="flex" justifyContent="space-between">
                                    <Typography variant="caption" color="error.main">Uploaded:</Typography>
                                    <Typography variant="caption" fontWeight="medium">
                                      {formatTrafficValue(iface.txBytes)}
                                    </Typography>
                                  </Box>
                                </Stack>
                                <Chip 
                                  label={iface.status} 
                                  size="small" 
                                  color="success"
                                  sx={{ mt: 1 }}
                                />
                              </CardContent>
                            </Card>
                          ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Stack>
              ) : (
                <Typography color="text.secondary">Data tidak tersedia untuk tab ini.</Typography>
              )}
            </TabPanel>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button variant="outlined" onClick={onClose} size="large">
          Tutup
        </Button>
        <Button 
          variant="contained" 
          size="large"
          onClick={fetchRouterDetails}
          disabled={loading}
          startIcon={<Refresh />}
        >
          Refresh Data
        </Button>
      </DialogActions>
      
      {/* PPP Connections Dialog */}
      <PPPConnectionsDialog 
        open={pppDialogOpen}
        onClose={() => setPppDialogOpen(false)}
        router={router}
      />
    </Dialog>
  );
};

export default RouterDetailDialog;
